import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Modal, Flex, Text, Button } from "@mantine/core";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { IconExternalLink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { LookAndFeelForm } from "@components/shared/LookAndFeelManagement";
import { ErrorView, PageTitle, MicrositeSettings } from "@components/shared";

export default function LookAndFeelDetail() {
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const params = useParams();
  const { id } = params;
  let [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const refererIds = {
    effortId: searchParams.get("effortId"),
    campaignId: searchParams.get("campaignId"),
    contesetId: searchParams.get("contestId"),
  };

  const hasRefererId = Object.keys(refererIds)
    .map((m) => refererIds[m])
    .find((f) => f !== undefined)
    ? true
    : false;

  const refererConfig = hasRefererId
    ? refererIds.effortId
      ? {
          entity: "effort",
          translation_key: "mixer:effort_singular",
          path: `/efforts/${refererIds.effortId}`,
        }
      : refererIds.campaignId
      ? {
          entity: "campaign",
          translation_key: "mixer:campaign_singular",
          path: `/campaigns/${refererIds.campaignId}`,
        }
      : refererIds.contesetId
      ? {
          entity: "contest",
          translation_key: "mixer:contest_singular",
          path: `/contests/${refererIds.contestId}`,
        }
      : null
    : null;

  useEffect(() => {
    fetchData();
  }, [id]);

  function fetchData() {
    axios
      .get(`/look-and-feel-templates/${id}/`)
      .then(({ data }) => {
        setInfo(data.response[0]);
      })
      .catch((err) => {
        setError(err);
        setInfo(null);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!info) return null;

  function onUpdate(formData) {
    const req = {
      ...formData,
      look_and_feel_template_id: id,
    };

    setSaving(true);

    axios
      .put(`/look-and-feel-templates/${id}/meta-data/`, req)
      .then(() => {
        toast.success(t("common:updated") + "!");
        setSaving(false);
        onClose();
        fetchData();
      })
      .catch((err) => {
        setSaving(false);
        toast.error(err);
      });
  }

  function onClose() {
    setEditing(false);
    setSaving(false);
  }

  return (
    <div>
      <Modal opened={editing} onClose={onClose}>
        <LookAndFeelForm
          title={info.title}
          description={info.description}
          onSubmit={onUpdate}
          loading={saving}
        />
      </Modal>
      <PageTitle title={info.title} subtitle={"Look And Feel Template"} />
      <Text
        style={{
          width: "80%",
        }}
      >
        {info.description}
      </Text>
      <MicrositeSettings lookAndFeelTemplateId={id} onUpdate={() => true} />
      <Flex gap="sm">
        <Button
          variant="light"
          color="gray"
          size="xs"
          mt="lg"
          onClick={() => setEditing(true)}
        >
          {t("common:edit")}
        </Button>
        {hasRefererId && (
          <Button
            component={Link}
            color="gray"
            variant="light"
            to={refererConfig.path}
            mt="lg"
            size="xs"
            rightSection={<IconExternalLink size={16} />}
          >
            {t("common:return_to")}{" "}
            {t(refererConfig.translation_key) || refererConfig.entity}
          </Button>
        )}
      </Flex>
    </div>
  );
}
